(() => {
  const tag = 'ea-ankauf-email';
  if (!window.customElements || customElements.get(tag)) return;
  customElements.define(tag, class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = {signal: this.controller.signal};
      this.config = JSON.parse(this.querySelector('[data-buyback-config]').textContent);
      this.form = this.querySelector('[data-buyback-form]');
      this.status = this.querySelector('[data-submit-status]');
      this.fallback = this.querySelector('[data-copy-fallback]');
      this.text = this.querySelector('[data-request-text]');
      this.mailLink = this.querySelector('[data-mail-link]');
      this.form.addEventListener('submit', event => { event.preventDefault(); this.prepareEmail(); }, options);
      this.querySelector('[data-copy-request]').addEventListener('click', () => this.copyRequest(), options);
      this.form.addEventListener('input', () => { this.status.textContent = ''; this.update(); }, options);
      this.form.addEventListener('change', () => this.update(), options);
      this.update();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
    }
    recipient() {
      const email = (this.dataset.email || '').trim();
      return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email) ? email : '';
    }
    estimate() {
      const fields = this.form.elements;
      const condition = this.form.querySelector('[name="condition"]:checked');
      if (!fields.uvp.value || !fields.age.value || !condition) return {state: 'empty'};
      if (condition.value === 'damaged') return {state: 'manual'};
      const price = Number(fields.uvp.value), ageFactor = Number(this.config.ageFactors?.[fields.age.value]);
      const share = Number(this.config.resaleShare), factor = Number(condition.dataset.factor);
      const handling = Number(this.config.handling), risk = Number(this.config.riskPercent), contribution = Number(this.config.contributionPercent), width = Number(this.config.bandWidth);
      const adjustment = Number(this.config.adjustment);
      if (![price, ageFactor, share, factor, adjustment, handling, risk, contribution, width].every(Number.isFinite) || price < 1 || price > 100000 || share < 0 || share > 100 || [handling,risk,contribution,width].some(v => v < 0) || risk > 100 || contribution > 100 || risk + contribution > 100 || factor <= 0 || factor > 1 || ageFactor < 0 || ageFactor > 100 || adjustment < -50 || adjustment > 50) return {state: 'manual'};
      const multiplier = factor * (ageFactor / 100) * (1 + adjustment / 100);
      const proceeds = price * share / 100 * multiplier;
      const maximum = proceeds * (1 - risk / 100 - contribution / 100) - handling;
      const high = Math.floor(maximum / 5) * 5;
      const low = Math.max(0, Math.floor((high - width) / 5) * 5);
      return high > 0 ? {state: 'ready', low, high} : {state: 'manual'};
    }
    estimateText() {
      const result = this.estimate();
      const format = value => new Intl.NumberFormat('de-DE', {style: 'currency', currency: 'EUR', maximumFractionDigits: 0}).format(value);
      if (result.state !== 'ready') return 'Individuelle Prüfung erforderlich';
      return `${format(result.low)} – ${format(result.high)}${this.config.examplePrices ? ' (Beispielrechnung)' : ''}`;
    }
    update() {
      const result = this.estimate();
      this.querySelector('[data-estimate-label]').textContent = this.config.examplePrices ? 'Beispielrechnung zur Orientierung' : 'Unverbindliche Ankaufsspanne';
      this.querySelector('[data-estimate-value]').textContent = result.state === 'ready' ? this.estimateText().replace(' (Beispielrechnung)', '') : '—';
      this.querySelector('[data-estimate-note]').textContent = result.state === 'ready' ? 'Unser persönliches Angebot folgt nach Prüfung deiner Angaben und Fotos.' : result.state === 'manual' ? 'Dieses Produkt prüfen wir individuell. Beschreibe Besonderheiten und füge deiner E-Mail Fotos bei.' : 'Gib UVP, Alter und Zustand an für deine erste Orientierung.';
      const recipient = this.recipient();
      this.querySelector('[data-email-prepare]').disabled = !recipient;
      this.querySelector('[data-copy-request]').disabled = false;
      if (!recipient) this.status.textContent = 'Die Kontaktadresse ist noch nicht eingerichtet. Bitte nutze die Kontaktseite des Shops.';
      if (!this.fallback.hidden) this.showText();
    }
    subject() {
      const fields = this.form.elements;
      return `Outdoor-Ankauf: ${fields.brand.value.trim()} ${fields.model.value.trim()}`.replace(/[\r\n]/g, ' ').slice(0, 140);
    }
    buildMessage() {
      const fields = this.form.elements;
      const optionText = field => field.selectedOptions[0]?.textContent.trim() || 'Keine Angabe';
      const condition = this.form.querySelector('[name="condition"]:checked');
      const conditionText = condition?.closest('label')?.querySelector('strong')?.textContent || 'Keine Angabe';
      return [
        'Hallo Eddy’s Adventure,', '', 'ich möchte euch meine gebrauchte Outdoor-Ausrüstung zum Ankauf anbieten.', '',
        `Produktart: ${optionText(fields.category)}`, `Marke: ${fields.brand.value.trim()}`, `Modell: ${fields.model.value.trim()}`,
        `Größe: ${fields.size.value.trim() || 'Keine Angabe'}`, `UVP laut Angabe: ${fields.uvp.value} EUR`, `Alter: ${optionText(fields.age)}`, `Zustand: ${conditionText}`,
        `Unverbindliche Orientierung: ${this.estimateText()}`, '', `Weitere Angaben: ${fields.notes.value.trim() || 'Keine'}`, '',
        `Name: ${fields.name.value.trim()}`, `E-Mail für Rückfragen: ${fields.email.value.trim()}`, '',
        'Bitte vor dem Absenden 2–3 Produktfotos als Anhänge hinzufügen: Gesamtansicht, Details und gegebenenfalls Gebrauchsspuren.', '',
        'Ich bitte um eine Ankaufprüfung. Mir ist bekannt, dass die berechnete Preisspanne kein verbindliches Angebot ist.',
        'Ich habe die Datenschutzhinweise zur Verarbeitung meiner Anfrage zur Kenntnis genommen.', '', 'Viele Grüße', fields.name.value.trim()
      ].join('\r\n');
    }
    showText() {
      this.fallback.hidden = false;
      this.text.value = `An: ${this.recipient()}\r\nBetreff: ${this.subject()}\r\n\r\n${this.buildMessage()}`;
    }
    async prepareEmail() {
      if (!this.form.reportValidity() || !this.recipient()) return;
      this.showText();
      let body = this.buildMessage();
      const buildUrl = value => `mailto:${this.recipient()}?subject=${encodeURIComponent(this.subject())}&body=${encodeURIComponent(value)}`;
      let url = buildUrl(body);
      if (url.length > 2000) {
        body = 'Hallo Eddy’s Adventure,\r\n\r\nich möchte euch gebrauchte Outdoor-Ausrüstung anbieten.\r\n\r\nBitte hier die vollständigen Angaben aus dem Website-Formular einfügen (Schaltfläche „Angaben kopieren“) und 2–3 Produktfotos anhängen.\r\n\r\nDie berechnete Preisspanne ist unverbindlich.';
        url = buildUrl(body);
        this.status.textContent = 'Deine Angaben sind zu lang für eine zuverlässige E-Mail-Vorbefüllung. Bitte klicke auf „Angaben kopieren“, füge den Text in deine E-Mail ein, hänge 2–3 Fotos an und sende selbst ab. Es wurde noch nichts übermittelt.';
      } else {
        this.status.textContent = 'Bitte prüfe den E-Mail-Entwurf, hänge 2–3 Fotos an und sende selbst ab. Es wurde noch nichts automatisch übermittelt. Öffnet sich kein E-Mail-Programm, nutze „Angaben kopieren“.';
      }
      this.mailLink.href = url;
      this.mailLink.click();
    }
    async copyRequest() {
      if (!this.form.reportValidity()) return;
      this.showText();
      try {
        if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
        await navigator.clipboard.writeText(this.text.value);
        this.status.textContent = 'Angaben kopiert. Füge sie in eine neue E-Mail ein, hänge 2–3 Fotos an und sende die Nachricht selbst ab.';
      } catch {
        if (!this.isConnected) return;
        this.text.focus();
        this.text.select();
        this.status.textContent = 'Bitte kopiere den markierten Text und füge ihn in deine E-Mail ein. Danach 2–3 Fotos anhängen und selbst absenden.';
      }
    }
  });
})();
